import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function RotationalMotionCalculator() {
  const [force, setForce] = useState("");
  const [radius, setRadius] = useState("");
  const [angle, setAngle] = useState("90");
  const [torqueResult, setTorqueResult] = useState<number | null>(null);

  const [angularVel, setAngularVel] = useState("");
  const [time, setTime] = useState("");
  const [angularDisp, setAngularDisp] = useState<number | null>(null);

  const calculateTorque = () => {
    const f = parseFloat(force);
    const r = parseFloat(radius);
    const theta = parseFloat(angle) * (Math.PI / 180);

    if (isNaN(f) || isNaN(r) || isNaN(theta)) {
      toast.error("Please enter valid values");
      return;
    }

    const torque = f * r * Math.sin(theta);
    setTorqueResult(torque);
    toast.success("Calculation complete!");
  };

  const calculateAngularMotion = () => {
    const omega = parseFloat(angularVel);
    const t = parseFloat(time);

    if (isNaN(omega) || isNaN(t)) {
      toast.error("Please enter valid values");
      return;
    }

    const displacement = omega * t;
    setAngularDisp(displacement);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setForce("");
    setRadius("");
    setAngle("90");
    setAngularVel("");
    setTime("");
    setTorqueResult(null);
    setAngularDisp(null);
  };

  return (
    <PageLayout calculatorName="Rotational Motion Calculator" calculatorPath="/calculator/rotational-motion">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Rotational Dynamics</CardTitle>
            <CardDescription>
              τ = rFsinθ | θ = ωt | α = Δω/Δt
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="torque" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="torque">Torque</TabsTrigger>
                <TabsTrigger value="angular">Angular Motion</TabsTrigger>
              </TabsList>

              <TabsContent value="torque" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="force">Force (N)</Label>
                    <Input
                      id="force"
                      type="number"
                      step="0.01"
                      placeholder="Enter force"
                      value={force}
                      onChange={(e) => setForce(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="radius">Radius/Distance (m)</Label>
                    <Input
                      id="radius"
                      type="number"
                      step="0.01"
                      placeholder="Enter radius"
                      value={radius}
                      onChange={(e) => setRadius(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="angle">Angle (degrees)</Label>
                    <Input
                      id="angle"
                      type="number"
                      step="0.1"
                      placeholder="Enter angle"
                      value={angle}
                      onChange={(e) => setAngle(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateTorque} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {torqueResult !== null && (
                  <>
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                      <CardHeader>
                        <CardTitle className="text-xl">Result</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-primary">
                          {torqueResult.toFixed(3)} N·m
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                          Torque
                        </p>
                      </CardContent>
                    </Card>
                    
                    <CalculatorSuperCalcCTA config={calculatorCTAs["rotational-motion"]} trigger={torqueResult} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="angular" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="angularVel">Angular Velocity (rad/s)</Label>
                    <Input
                      id="angularVel"
                      type="number"
                      step="0.01"
                      placeholder="Enter angular velocity"
                      value={angularVel}
                      onChange={(e) => setAngularVel(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time">Time (s)</Label>
                    <Input
                      id="time"
                      type="number"
                      step="0.01"
                      placeholder="Enter time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateAngularMotion} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {angularDisp !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {angularDisp.toFixed(3)} rad
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Angular Displacement ({(angularDisp * 180 / Math.PI).toFixed(2)}°)
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Torque (τ) = Force × Distance × sin(angle)</p>
                <p>• Angular displacement (θ) = Angular velocity × time</p>
                <p>• 1 revolution = 2π radians = 360°</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 10 N force at 0.5 m with 90° angle = 5 N·m torque
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
